import type { BarState, ConstraintIntent } from '../../extensions/types.js';

export interface DragHandler {
  readonly intent: ConstraintIntent;
  readonly initialState: BarState;
  /**
   * Produces the proposed bar state for the given time delta (in ms)
   * from the drag origin. Pure — safe to call every pointermove frame.
   */
  compute(deltaMs: number): BarState;
}

export class MoveHandler implements DragHandler {
  readonly intent: ConstraintIntent = 'move';

  constructor(readonly initialState: BarState) {}

  compute(deltaMs: number): BarState {
    return {
      ...this.initialState,
      startMs: this.initialState.startMs + deltaMs,
      endMs: this.initialState.endMs + deltaMs,
    };
  }
}

export class ResizeStartHandler implements DragHandler {
  readonly intent: ConstraintIntent = 'resize-start';

  constructor(readonly initialState: BarState) {}

  compute(deltaMs: number): BarState {
    return {
      ...this.initialState,
      startMs: this.initialState.startMs + deltaMs,
    };
  }
}

export class ResizeEndHandler implements DragHandler {
  readonly intent: ConstraintIntent = 'resize-end';

  constructor(readonly initialState: BarState) {}

  compute(deltaMs: number): BarState {
    return {
      ...this.initialState,
      endMs: this.initialState.endMs + deltaMs,
    };
  }
}

/**
 * Drag-to-create starts from an anchor timestamp; the user sweeps the
 * pointer left or right to define the opposite edge. The handler always
 * keeps `startMs <= endMs` regardless of drag direction.
 */
export class CreateHandler implements DragHandler {
  readonly intent: ConstraintIntent = 'create';
  readonly initialState: BarState;
  private readonly anchorMs: number;

  constructor(rowId: string, anchorMs: number) {
    this.anchorMs = anchorMs;
    this.initialState = { rowId, startMs: anchorMs, endMs: anchorMs };
  }

  compute(deltaMs: number): BarState {
    const currentMs = this.anchorMs + deltaMs;
    if (currentMs >= this.anchorMs) {
      return {
        ...this.initialState,
        startMs: this.anchorMs,
        endMs: currentMs,
      };
    }
    return {
      ...this.initialState,
      startMs: currentMs,
      endMs: this.anchorMs,
    };
  }
}
